import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { FeatureFlagsProvider, useFeatureFlags } from "./contexts/FeatureFlagsContext";
import { LiveTrackingProvider } from "./contexts/LiveTrackingContext";
import { GeofencingProvider } from "./contexts/GeofencingContext";
import { AnalyticsProvider } from "./contexts/AnalyticsContext";
import { DashboardProvider } from "./contexts/DashboardContext";
import { MediaProvider } from "./contexts/MediaContext";
import { ChatProvider } from "./contexts/ChatContext";
import { CallProvider } from "./contexts/CallContext";
import { GroupCallProvider } from "./contexts/GroupCallContext";
import { CallOverlay } from "./components/calls/CallOverlay";
import { GroupCallOverlay } from "./components/calls/GroupCallOverlay";
import { AdminPanelProvider } from "./contexts/AdminPanelContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import { Layout } from "./components/Layout";
import { Login } from "./pages/Login";
import QuickLogin from "./pages/QuickLogin";
import LinkRevoked from "./pages/LinkRevoked";
import AcceptInvite from "./pages/AcceptInvite";
import ResetPassword from "./pages/ResetPassword";
import FollowDevice from "./pages/FollowDevice";
import PublicTrackingPage from "./pages/PublicTrackingPage";
import PublicEvidencePage from "./pages/PublicEvidencePage";
import { Dashboard } from "./pages/Dashboard";
import { LiveTracking } from "./pages/LiveTracking";
import { Media } from "./pages/Media";
import { Geofencing } from "./pages/Geofencing";
import { Analytics } from "./pages/Analytics";
import { Operations } from "./pages/Operations";
import { Fleet } from "./pages/Fleet";
import { People } from "./pages/People";
import { Chat } from "./pages/Chat";
import MyContact from "./pages/MyContact";
import { Installation } from "./pages/Installation";
import { Settings } from "./pages/Settings";
import { AdminPanel } from "./pages/AdminPanel";
import { DeviceManagement } from "./pages/admin/DeviceManagement";
import { UserManagement } from "./pages/admin/UserManagement";
import { FeatureManagement } from "./pages/admin/FeatureManagement";
import { Commands } from "./pages/PlaceholderPages";
import { Loader2, Shield } from "lucide-react";
import { registerServiceWorker, isPushSupported } from "./utils/pushNotifications";
import { getLandingPath } from "./config/landingPages";

// Protected Route wrapper with page-level permission check
const ProtectedRoute = ({ children, requiredFeature }) => {
  const { isAuthenticated, loading } = useAuth();
  const { features, loading: featuresLoading } = useFeatureFlags();

  // Show loading while auth or features are loading
  if (loading || featuresLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-brand-blue" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Wait for features to be actually populated (not just loading=false)
  // This prevents the brief "Access Denied" flash
  if (requiredFeature && (!features.pages || Object.keys(features.pages).length === 0)) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-brand-blue" />
      </div>
    );
  }

  // SECURITY FIX: Check page-level permission
  if (requiredFeature) {
    const hasAccess = features.pages?.[requiredFeature] === true;
    if (!hasAccess) {
      // Redirect to dashboard with access denied
      return (
        <Layout>
          <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
            <div className="text-center">
              <Shield className="w-16 h-16 mx-auto mb-4 text-red-500" />
              <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
              <p className="text-muted-foreground mb-4">
                You don't have permission to access this page.
              </p>
              <p className="text-sm text-muted-foreground">
                Contact your administrator to request access.
              </p>
            </div>
          </div>
        </Layout>
      );
    }
  }

  return <Layout>{children}</Layout>;
};

// Follow Device Route — no layout, just auth check
const FollowDeviceRoute = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen bg-background"><Loader2 className="w-8 h-8 animate-spin text-brand-blue" /></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <FollowDevice />;
};

// Smart redirect to first available page
const SmartRedirect = () => {
  const { isAuthenticated, loading } = useAuth();
  const { features, loading: featuresLoading } = useFeatureFlags();

  if (loading || featuresLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-brand-blue" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Wait for features to be loaded (check if pages object has keys)
  if (!features.pages || Object.keys(features.pages).length === 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-brand-blue" />
      </div>
    );
  }

  const landingPath = getLandingPath(features.landing_page, features);
  if (landingPath) return <Navigate to={landingPath} replace />;

  // Fallback - show access denied if no pages are enabled
  return (
    <Layout>
      <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-2xl font-bold mb-2">No Access</h2>
          <p className="text-muted-foreground mb-4">
            You don't have access to any pages.
          </p>
          <p className="text-sm text-muted-foreground">
            Contact your administrator to request access.
          </p>
        </div>
      </div>
    </Layout>
  );
};

const LegacyLiveTrackingRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/tracking${location.search || ''}`} replace />;
};

// Admin Route wrapper - allows access for admins, managers, and end users
const AdminRoute = ({ children }) => {
  const { user, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-brand-blue" />
      </div>
    );
  }

  // Allow super_admin, admin, manager, and end_user to access admin routes
  const allowedRoles = ['super_admin', 'admin', 'manager', 'end_user'];
  const hasAccess = user && allowedRoles.includes(user.role);

  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route wrapper (redirects to preferred landing page if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-brand-blue" />
      </div>
    );
  }

  // ?switch=1 — explicit "sign in as a different user" (e.g. from the revoked
  // quick-link page): show the login form even when this browser already has a session.
  const wantsSwitch = new URLSearchParams(location.search).get('switch') === '1';
  if (isAuthenticated && !wantsSwitch) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  const navigate = useNavigate();

  // Register service worker and listen for push notification clicks
  useEffect(() => {
    if (isPushSupported()) {
      registerServiceWorker();
    }
    const handler = (event) => {
      if (event.data?.type === 'PUSH_NOTIFICATION_CLICK' && event.data?.url) {
        navigate(event.data.url);
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, [navigate]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/quick-login/:token" element={<QuickLogin />} />
      <Route path="/link-revoked" element={<LinkRevoked />} />
      <Route path="/accept-invite/:token" element={<AcceptInvite />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/track/:token" element={<PublicTrackingPage />} />
      <Route path="/evidence/:token" element={<PublicEvidencePage />} />

      {/* Protected Routes with Page-Level Permission Checks */}
      <Route path="/follow-device" element={<FollowDeviceRoute />} />
      <Route path="/live-tracking" element={<LegacyLiveTrackingRedirect />} />
      <Route path="/dashboard" element={<ProtectedRoute requiredFeature="dashboard_page"><Dashboard /></ProtectedRoute>} />
      <Route path="/tracking" element={<ProtectedRoute requiredFeature="live_tracking_page"><LiveTracking /></ProtectedRoute>} />
      <Route path="/media" element={<ProtectedRoute requiredFeature="media_page"><Media /></ProtectedRoute>} />
      <Route path="/geofencing" element={<ProtectedRoute requiredFeature="geofencing_page"><Geofencing /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute requiredFeature="analytics_page"><Analytics /></ProtectedRoute>} />
      <Route path="/people" element={<ProtectedRoute requiredFeature="people_page"><People /></ProtectedRoute>} />
      <Route path="/fleet" element={<ProtectedRoute requiredFeature="fleet_page"><Fleet /></ProtectedRoute>} />
      <Route path="/operations" element={<ProtectedRoute requiredFeature="operations_page"><Operations /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute requiredFeature="chat_page"><Chat /></ProtectedRoute>} />
      <Route path="/my-contact" element={<ProtectedRoute requiredFeature="my_contact_page"><MyContact /></ProtectedRoute>} />
      <Route path="/installation" element={<ProtectedRoute requiredFeature="installation_page"><Installation /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute requiredFeature="settings_page"><Settings /></ProtectedRoute>} />

      {/* Admin Panel Route (New) */}
      <Route path="/admin-panel" element={<ProtectedRoute requiredFeature="admin_panel_page"><AdminRoute><AdminPanel /></AdminRoute></ProtectedRoute>} />

      {/* Legacy Admin Routes (kept for backward compatibility) */}
      <Route path="/admin/users" element={<ProtectedRoute><AdminRoute><UserManagement /></AdminRoute></ProtectedRoute>} />
      <Route path="/admin/devices" element={<ProtectedRoute><AdminRoute><DeviceManagement /></AdminRoute></ProtectedRoute>} />
      <Route path="/admin/commands" element={<ProtectedRoute><AdminRoute><Commands /></AdminRoute></ProtectedRoute>} />
      <Route path="/admin/features" element={<ProtectedRoute><AdminRoute><FeatureManagement /></AdminRoute></ProtectedRoute>} />
      
      {/* Temporary Demo Route */}

      {/* Default redirect - Smart redirect to first available page */}
      <Route path="/" element={<SmartRedirect />} />
      <Route path="*" element={<SmartRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <FeatureFlagsProvider>
          <LiveTrackingProvider>
          <GeofencingProvider>
          <AnalyticsProvider>
          <DashboardProvider>
          <MediaProvider>
          <ChatProvider>
          <CallProvider>
          <GroupCallProvider>
          <AdminPanelProvider>
          <SettingsProvider>
          <BrowserRouter>
            <AppRoutes />
            <CallOverlay />
            <GroupCallOverlay />
            <Toaster 
              position="top-right" 
              richColors 
              closeButton
              className="app-sonner-toaster z-[2147483647]"
              style={{ zIndex: 2147483647 }}
              toastOptions={{
                style: { fontFamily: 'Inter, sans-serif' },
              }}
            />
          </BrowserRouter>
          </SettingsProvider>
          </AdminPanelProvider>
          </GroupCallProvider>
          </CallProvider>
          </ChatProvider>
          </MediaProvider>
          </DashboardProvider>
          </AnalyticsProvider>
          </GeofencingProvider>
          </LiveTrackingProvider>
        </FeatureFlagsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
